import { useQuery } from "@tanstack/react-query";

/**
 * One rule's page: its live record against its own backtest, whether the
 * market agrees with it, and where it sits on the 25/50/75/100-bet review
 * ladder. A rule is judged against its research, not against the close; the
 * close only says whether the market agreed.
 */

export interface RuleBet {
  readonly bet_id: string;
  readonly execution_mode: string;
  readonly game_starts_at: string | null;
  readonly price: number | string | null;
  readonly result: string | null;
  readonly market_move: number | string | null;
  /** Live placed bets only: minutes from alert to placement, and the placed
   * price over the first price (negative = placed worse). */
  readonly minutes_to_place?: number | string | null;
  readonly price_change?: number | string | null;
}

export interface RuleHistory {
  readonly bets: readonly RuleBet[];
  readonly backtest: {
    readonly bt_wins: number | string | null;
    readonly bt_losses: number | string | null;
    readonly bt_strike_rate: number | string | null;
    readonly bt_roi: number | string | null;
    readonly bt_basis: string | null;
  } | null;
  readonly refused: number;
}

export const CHECKPOINTS = [25, 50, 75, 100] as const;
/** Same dead band the analytics summaries use for "the market moved". */
const MOVE_THRESHOLD = 0.001;

const num = (value: number | string | null | undefined): number | null =>
  value === null || value === undefined || value === ""
    ? null
    : Number.isFinite(Number(value))
      ? Number(value)
      : null;

export function summariseRule(history: RuleHistory) {
  const graded = history.bets.filter(
    (bet) => bet.result === "WIN" || bet.result === "LOSS",
  );
  const settled = history.bets.filter((bet) => bet.result !== null);
  const wins = graded.filter((bet) => bet.result === "WIN").length;
  const losses = graded.length - wins;
  const prices = graded
    .map((bet) => num(bet.price))
    .filter((price): price is number => price !== null && price > 1);
  const avgPrice =
    prices.length === 0
      ? null
      : prices.reduce((sum, price) => sum + price, 0) / prices.length;
  // Flat one unit a bet at the first price, the same for live and tracked
  // rules, so the two can be read side by side.
  let running = 0;
  const curve = settled.map((bet) => {
    const price = num(bet.price) ?? 0;
    running +=
      bet.result === "WIN" ? price - 1 : bet.result === "LOSS" ? -1 : 0;
    return running;
  });
  const moves = history.bets
    .map((bet) => num(bet.market_move))
    .filter((move): move is number => move !== null);
  const toward = moves.filter((move) => move > MOVE_THRESHOLD).length;
  const against = moves.filter((move) => move < -MOVE_THRESHOLD).length;
  const reached = CHECKPOINTS.filter((mark) => settled.length >= mark).at(-1);
  const next = CHECKPOINTS.find((mark) => settled.length < mark);
  const minutes = history.bets
    .map((bet) => num(bet.minutes_to_place))
    .filter((value): value is number => value !== null)
    .toSorted((a, b) => a - b);
  const changes = history.bets
    .map((bet) => num(bet.price_change))
    .filter((value): value is number => value !== null);
  const btRoi = num(history.backtest?.bt_roi);
  const btStrike = num(history.backtest?.bt_strike_rate);
  return {
    settled: settled.length,
    wins,
    losses,
    units: running,
    avgPrice,
    strike: graded.length === 0 ? null : wins / graded.length,
    breakEven: avgPrice === null ? null : 1 / avgPrice,
    toward,
    against,
    reached: reached ?? null,
    next: next ?? null,
    curve,
    btRoi,
    btStrike,
    btBets:
      num(history.backtest?.bt_wins) === null
        ? null
        : (num(history.backtest?.bt_wins) ?? 0) +
          (num(history.backtest?.bt_losses) ?? 0),
    btBasis: history.backtest?.bt_basis ?? null,
    refused: history.refused,
    placed: minutes.length,
    medianMinutesToPlace:
      minutes.length === 0 ? null : minutes[Math.floor(minutes.length / 2)]!,
    placedWithin15: minutes.filter((value) => value <= 15).length,
    avgPriceChange:
      changes.length === 0
        ? null
        : changes.reduce((sum, value) => sum + value, 0) / changes.length,
  };
}

type Summary = ReturnType<typeof summariseRule>;

const pct = (value: number | null) =>
  value === null ? "—" : `${(value * 100).toFixed(1)}%`;
const signedPct = (value: number | null) =>
  value === null ? "—" : `${value >= 0 ? "+" : ""}${(value * 100).toFixed(1)}%`;
const units = (value: number) => `${value >= 0 ? "+" : ""}${value.toFixed(2)}u`;

/** The message for the rules team at a checkpoint, ready to paste. */
export function rulesTeamNote(
  rule: string,
  sport: string,
  tracked: boolean,
  summary: Summary,
): string {
  const moved = summary.toward + summary.against;
  return [
    `${rule} (${sport}, ${tracked ? "tracked, no stake" : "live"})${
      summary.reached === null
        ? ` - ${summary.settled} settled, first checkpoint at 25`
        : ` - checkpoint ${summary.reached} reached (${summary.settled} settled)`
    }`,
    `Record ${summary.wins}-${summary.losses} at avg ${
      summary.avgPrice?.toFixed(2) ?? "—"
    }: strike ${pct(summary.strike)} vs break-even ${pct(
      summary.breakEven,
    )}, flat ${units(summary.units)}${
      summary.settled === 0
        ? ""
        : ` (${signedPct(summary.units / summary.settled)} a bet)`
    }.`,
    summary.btStrike === null && summary.btRoi === null
      ? "Backtest: none on record."
      : `Backtest: strike ${pct(summary.btStrike)}, return ${signedPct(
          summary.btRoi,
        )}${summary.btBets === null ? "" : ` over ${summary.btBets} bets`}${
          summary.btBasis === null ? "" : ` (${summary.btBasis})`
        }.`,
    moved === 0
      ? "Whole market: no measurable move yet."
      : `Whole market moved toward us on ${summary.toward} of ${moved} (${pct(
          summary.toward / moved,
        )}).`,
    `Refused by the minimum-odds gate: ${summary.refused}.`,
  ].join("\n");
}

/** Cumulative flat units against the backtest's expectation and a 2-sigma band. */
function RuleChart({ summary }: { summary: Summary }) {
  const n = summary.curve.length;
  if (n < 2)
    return <p className="tile-empty">Not enough settled bets to chart.</p>;
  const price = summary.avgPrice ?? 1.9;
  const p = summary.btStrike ?? summary.strike ?? 0.5;
  // Spread of one flat bet's return at this price and strike rate.
  const sd = price * Math.sqrt(p * (1 - p));
  const expected = (i: number) => (summary.btRoi ?? 0) * i;
  const band = (i: number) => 2 * sd * Math.sqrt(i);
  const hasBacktest = summary.btRoi !== null;
  const ys = [
    ...summary.curve,
    ...(hasBacktest ? [expected(n) + band(n), expected(n) - band(n)] : []),
    0,
  ];
  const top = Math.max(...ys);
  const bottom = Math.min(...ys);
  const width = 640;
  const height = 180;
  const pad = 28;
  const x = (i: number) => pad + ((width - 2 * pad) * i) / n;
  const y = (value: number) =>
    pad + ((height - 2 * pad) * (top - value)) / (top - bottom || 1);
  const points = [0, ...summary.curve]
    .map((value, i) => `${x(i).toFixed(1)},${y(value).toFixed(1)}`)
    .join(" ");
  const bandPath = hasBacktest
    ? [
        ...Array.from({ length: n + 1 }, (_, i) => [
          x(i),
          y(expected(i) + band(i)),
        ]),
        ...Array.from({ length: n + 1 }, (_, i) => [
          x(n - i),
          y(expected(n - i) - band(n - i)),
        ]),
      ]
        .map(
          ([px, py], i) =>
            `${i === 0 ? "M" : "L"}${px!.toFixed(1)},${py!.toFixed(1)}`,
        )
        .join(" ") + " Z"
    : null;
  return (
    <svg
      className="rule-chart"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={`Cumulative flat units over ${n} settled bets`}
    >
      {bandPath === null ? null : (
        <path d={bandPath} className="rule-chart-band" />
      )}
      <line
        x1={pad}
        x2={width - pad}
        y1={y(0)}
        y2={y(0)}
        className="rule-chart-zero"
      />
      {hasBacktest ? (
        <line
          x1={x(0)}
          x2={x(n)}
          y1={y(0)}
          y2={y(expected(n))}
          className="rule-chart-expected"
        />
      ) : null}
      <polyline points={points} className="rule-chart-line" />
      <text
        x={x(n)}
        y={y(summary.units) - 6}
        textAnchor="end"
        className="rule-chart-label"
      >
        {units(summary.units)}
      </text>
      <text x={pad} y={height - 8} className="rule-chart-label">
        bet 1
      </text>
      <text
        x={width - pad}
        y={height - 8}
        textAnchor="end"
        className="rule-chart-label"
      >
        bet {n}
      </text>
    </svg>
  );
}

export function RulePanel({
  sport,
  rule,
  tracked,
  load,
  onClose,
}: {
  sport: string;
  rule: string;
  tracked: boolean;
  load: (sport: string, rule: string) => Promise<RuleHistory>;
  onClose: () => void;
}) {
  const history = useQuery({
    queryKey: ["rule-history", sport, rule],
    queryFn: () => load(sport, rule),
  });
  const summary =
    history.data === undefined ? null : summariseRule(history.data);
  const note =
    summary === null ? "" : rulesTeamNote(rule, sport, tracked, summary);
  return (
    <section
      className="card board-wide rule-panel"
      aria-label={`${rule} rule page`}
    >
      <header className="rule-panel-head">
        <div>
          <span className="eyebrow">
            {sport} · {tracked ? "Tracked, no stake" : "Live"}
          </span>
          <h3>{rule}</h3>
        </div>
        <button
          type="button"
          className="icon-button"
          aria-label="Close rule page"
          onClick={onClose}
        >
          ×
        </button>
      </header>
      {history.isError ? (
        <p role="alert">The rule history could not be loaded.</p>
      ) : summary === null ? (
        <p className="tile-empty">Loading…</p>
      ) : (
        <>
          <dl className="rule-facts">
            <div>
              <dt>Record</dt>
              <dd>
                {summary.wins}-{summary.losses}
                <small>{summary.settled} settled</small>
              </dd>
            </div>
            <div>
              <dt>Strike vs break-even</dt>
              <dd>
                {pct(summary.strike)}
                <small>needs {pct(summary.breakEven)}</small>
              </dd>
            </div>
            <div>
              <dt>Flat units</dt>
              <dd>
                {units(summary.units)}
                <small>1u a bet at the first price</small>
              </dd>
            </div>
            <div>
              <dt>Backtest</dt>
              <dd>
                {summary.btRoi === null && summary.btStrike === null
                  ? "none"
                  : `${pct(summary.btStrike)} · ${signedPct(summary.btRoi)}`}
                <small>{summary.btBasis ?? "no research record"}</small>
              </dd>
            </div>
            <div>
              <dt>Market agreed</dt>
              <dd>
                {summary.toward + summary.against === 0
                  ? "—"
                  : `${summary.toward}-${summary.against}`}
                <small>toward-against, whole market</small>
              </dd>
            </div>
            <div>
              <dt>Time to place</dt>
              <dd>
                {summary.medianMinutesToPlace === null
                  ? "—"
                  : `${Math.round(summary.medianMinutesToPlace)} min`}
                <small>
                  {summary.placed === 0
                    ? "no live placements"
                    : `${summary.placedWithin15} of ${summary.placed} within 15 min · price ${signedPct(summary.avgPriceChange)}`}
                </small>
              </dd>
            </div>
            <div>
              <dt>Checkpoint</dt>
              <dd>
                {summary.reached === null ? "—" : `${summary.reached} reached`}
                <small>
                  {summary.next === null
                    ? "past 100 - full review"
                    : `${summary.next - summary.settled} to ${summary.next}`}
                </small>
              </dd>
            </div>
          </dl>
          <RuleChart summary={summary} />
          <p className="rule-chart-key">
            Line: flat units so far.{" "}
            {summary.btRoi === null
              ? "No backtest return on record, so no expected band."
              : "Dashed: what the backtest return would have made. Shaded: the range luck alone covers (2 sigma) - outside it, the rule is not behaving like its research."}
          </p>
          <label className="rule-note">
            For the rules team
            <textarea readOnly value={note} rows={6} />
          </label>
          <button
            type="button"
            className="button"
            onClick={() => void navigator.clipboard?.writeText(note)}
          >
            Copy note
          </button>
        </>
      )}
    </section>
  );
}
